/**
 * Staff "Record outcome" panel for an evaluation-day booking (migration 0099).
 *
 * Mounted on the booking detail for `type === 'service'` bookings; it asks
 * GET /api/bookings/:id/assessment and renders nothing when the booking is
 * not an evaluation. Submitting POSTs the verdict + optional behaviour
 * observation (traits feed the pet's derived behaviour profile).
 */
import {
  ASSESSMENT_STATUS_LABEL,
  ASSESSMENT_STATUS_TONE,
  type AssessmentStatus,
  type BookingAssessmentView,
  getBookingAssessment,
  recordBookingAssessmentOutcome,
  traitLabel,
} from '@/lib/assessments';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

export function AssessmentStatusChip({ status }: { status: AssessmentStatus }) {
  return (
    <span
      data-testid="assessment-status-chip"
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${ASSESSMENT_STATUS_TONE[status] ?? ASSESSMENT_STATUS_TONE.not_required}`}
    >
      {ASSESSMENT_STATUS_LABEL[status] ?? status}
    </span>
  );
}

function formatWallClock(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Facility wall clock encoded as Z (grooming convention) — render in UTC.
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AssessmentOutcomePanel({
  bookingId,
  onRecorded,
}: {
  bookingId: string;
  onRecorded?: (status: AssessmentStatus) => void;
}) {
  const [view, setView] = useState<BookingAssessmentView | null>(null);
  const [outcome, setOutcome] = useState<'passed' | 'failed'>('passed');
  const [notes, setNotes] = useState('');
  const [traits, setTraits] = useState<Set<string>>(new Set());
  const [obsNotes, setObsNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await getBookingAssessment(bookingId));
    } catch {
      setView({ isAssessment: false });
    }
  }, [bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!view || !view.isAssessment || !view.pet) return null;

  const toggleTrait = (t: string) => {
    const next = new Set(traits);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    setTraits(next);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await recordBookingAssessmentOutcome(bookingId, {
        outcome,
        notes: notes.trim().length > 0 ? notes.trim() : null,
        observations:
          traits.size > 0 ? { traits: Array.from(traits), notes: obsNotes.trim() || null } : null,
      });
      onRecorded?.(res.pet.assessmentStatus);
      setNotes('');
      setObsNotes('');
      setTraits(new Set());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the outcome.');
    } finally {
      setSubmitting(false);
    }
  };

  const alreadyRecorded = view.bookingStatus === 'completed';

  return (
    <div className="border-t border-border px-5 py-4" data-testid="assessment-outcome-panel">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        <ClipboardCheck className="size-3.5" strokeWidth={1.75} />
        Evaluation day
      </p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="num font-medium">{formatWallClock(view.scheduledAt)}</span>
        {view.durationMin != null && (
          <span className="text-xs text-muted-foreground">
            <span className="num">{view.durationMin}</span> min
          </span>
        )}
        {view.ownerMustStay && <span className="text-xs text-muted-foreground">Owner stays</span>}
        <AssessmentStatusChip status={view.pet.assessmentStatus} />
      </div>

      {view.outcomes && view.outcomes.length > 0 && (
        <ul
          className="mt-3 space-y-1 text-xs text-muted-foreground"
          data-testid="assessment-history"
        >
          {view.outcomes.map((o) => (
            <li key={o.id}>
              <span className="font-medium text-foreground">
                {o.outcome === 'passed' ? 'Passed' : 'Not passed'}
              </span>{' '}
              <span className="num">{new Date(o.createdAt).toLocaleDateString('en-GB')}</span>
              {o.notes ? ` — ${o.notes}` : ''}
            </li>
          ))}
        </ul>
      )}

      {!alreadyRecorded && (
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <fieldset className="flex gap-4 text-sm">
            <legend className="mb-1 text-xs font-medium">Outcome</legend>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`assessment-outcome-${bookingId}`}
                value="passed"
                checked={outcome === 'passed'}
                onChange={() => setOutcome('passed')}
              />
              Passed
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name={`assessment-outcome-${bookingId}`}
                value="failed"
                checked={outcome === 'failed'}
                onChange={() => setOutcome('failed')}
              />
              Not passed
            </label>
          </fieldset>
          <label className="block text-xs font-medium">
            Notes for the owner
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              rows={3}
              maxLength={4000}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-normal"
              placeholder="How did it go? This goes in the email."
            />
          </label>
          {view.traits && view.traits.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium">Behaviour observed</p>
              <div className="flex flex-wrap gap-1.5">
                {view.traits.map((t) => (
                  <label
                    key={t}
                    className={`cursor-pointer rounded-full border px-2.5 py-0.5 text-xs ${
                      traits.has(t)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={traits.has(t)}
                      onChange={() => toggleTrait(t)}
                      aria-label={traitLabel(t)}
                    />
                    {traitLabel(t)}
                  </label>
                ))}
              </div>
              {traits.size > 0 && (
                <input
                  type="text"
                  value={obsNotes}
                  onChange={(e) => setObsNotes(e.currentTarget.value)}
                  maxLength={2000}
                  placeholder="Observation notes (staff only)"
                  aria-label="Observation notes"
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
                />
              )}
            </div>
          )}
          {error && (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {submitting && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
            Record outcome
          </button>
        </form>
      )}
    </div>
  );
}
